import { useEffect, useState } from "react"
import { captureMessage } from "@sentry/react"

import PurchaseModal from "app/components/subscriptions/purchase_modal"
import Modal from "shared/components/modal"
import closeX from "images/components/x.svg"
import logIFTTTNextAction from "app/lib/ifttt_next_log_action"

import "./index.scss"

interface URLs {
  modalSubscriptionUrl: string
  newProSubscriptionUrl: string
  newProPlusSubscriptionUrl: string
}

interface SubscriptionProps {
  assets: { iftttSvgUrl: string; proBadgeSvgUrl: string; proPlusBadgeSvgUrl: string }
  browser_type: string
  create_stripe_customer_url: string
  eligible_for_trial: boolean
  email: string
  initial_promo_code: string | null
  intermediate_pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  pro: { product_id: string; plan_id: string; tier: string; monthly_plan: object; yearly_plan: object }
  subscriptions_path: string
}

interface Props {
  modalCopyObj: {
    checkoutModalTarget: "intermediate_pro" | "pro_plus"
    copy: string
    title: string
  }
  onClose: () => void
  onUpgrade: (a: string) => void
  step: string
  trackLocationType: string
  urls: URLs
  visible: boolean
}

const CheckoutModal = ({ modalCopyObj, onClose, onUpgrade, step, trackLocationType, urls, visible }: Props) => {
  const [subscriptionProps, updateSubscriptionProps]: [SubscriptionProps, (a: SubscriptionProps) => void] =
    useState<SubscriptionProps>({} as SubscriptionProps)
  const [subscriptionPropsLoaded, setSubscriptionPropsLoaded]: [boolean, (a: boolean) => void] = useState(false)
  const { checkoutModalTarget, title, copy } = modalCopyObj

  const loadSubscriptionProps = () => {
    fetch(urls.modalSubscriptionUrl, {
      method: "get",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      redirect: "error",
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status)
        }

        return response.json()
      })
      .then(result => {
        updateSubscriptionProps(result)
        setSubscriptionPropsLoaded(true)
      })
      .catch(() => {
        const modalTargetPlan = modalCopyObj.checkoutModalTarget
        // In case that we have an issue load data to be used in SignUpAndPurchase, not block user. Redirect them to checkout page.
        captureMessage("Checkout modal failed to load subscription data and redirect user to checkout page.", {
          tags: { user_id: window.App.userId },
          extra: { modalTargetPlan },
        })
        window.location.assign(
          modalCopyObj.checkoutModalTarget === "intermediate_pro"
            ? urls.newProSubscriptionUrl
            : urls.newProPlusSubscriptionUrl
        )
      })
  }

  useEffect(() => {
    loadSubscriptionProps()
  }, [])

  const onCloseHandleClick = () => {
    logIFTTTNextAction("click", {
      object_type: "checkout_inline",
      object_id: "cancel_button",
      location_type: trackLocationType,
    })
    onClose()
  }

  return subscriptionPropsLoaded ? (
    <div styleName="modal-container checkout-on-modal" className="checkout-on-modal">
      <Modal show={visible} includeCloseX={false}>
        <section styleName="checkout-container">
          <section styleName="left" className={`illustration-${step} ${modalCopyObj.checkoutModalTarget}`} />
          <section styleName="right">
            <div styleName="close" onClick={onCloseHandleClick}>
              <span
                className="close-x"
                dangerouslySetInnerHTML={{
                  __html: closeX,
                }}
              />
            </div>
            <header styleName="onboarding-header" className="pb2 pb0--phone">
              <h1 styleName="ifttt">
                <img src={subscriptionProps["assets"].iftttSvgUrl} alt="IFTTT logo" width="122" height="37" />
                {checkoutModalTarget === "pro" ? (
                  <img src={subscriptionProps["assets"].proPlusBadgeSvgUrl} alt="Pro+ badge" width="91" height="53" />
                ) : (
                  <img src={subscriptionProps["assets"].proBadgeSvgUrl} alt="Pro badge" width="91" height="53" />
                )}
              </h1>
              <h2 className="alt-title" styleName="modal-title">
                {title}
              </h2>
              <p className="copy txt-body-3">{copy}</p>
            </header>
            <PurchaseModal
              onClose={onClose}
              onUpgrade={() => onUpgrade(checkoutModalTarget)}
              subscriptionProps={subscriptionProps}
              target={checkoutModalTarget}
            />
          </section>
        </section>
      </Modal>
    </div>
  ) : null
}

export default CheckoutModal
